import { useEffect, useRef, useState } from 'react';
import { resolveFxMultiplier } from '../../../../commonSubEntities/accounting/frankfurterFx';
import { bpAccountingRow } from '../../../../../../models/entities/businessPartner/accounting';

/**
 * Per-row state for "Total profit (base)". Each row converts every currency in
 * `profitByCurrency` to the BP's base currency using a shared FX cache.
 */
export type ProfitInBaseState =
    | { status: 'no_base' }
    | { status: 'loading' }
    | { status: 'ok'; total: number }
    | { status: 'partial'; total: number; missingCurrencies: string[] }
    | { status: 'error' };

export function useProfitInBase(rows: bpAccountingRow[], baseCurrency: string | null | undefined) {
    const [byShipment, setByShipment] = useState<Record<string, ProfitInBaseState>>({});
    const fxCacheRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
        let cancelled = false;
        const base = (baseCurrency ?? '').trim().toUpperCase();

        if (!base) {
            const initial: Record<string, ProfitInBaseState> = {};
            for (const r of rows) initial[r.shipmentId] = { status: 'no_base' };
            setByShipment(initial);
            return () => {
                cancelled = true;
            };
        }

        const initial: Record<string, ProfitInBaseState> = {};
        for (const r of rows) initial[r.shipmentId] = { status: 'loading' };
        setByShipment(initial);

        const run = async () => {
            const cache = fxCacheRef.current;

            await Promise.all(
                rows.map(async (row) => {
                    const entries = Object.entries(row.profitByCurrency ?? {}).filter(
                        ([, v]) => Number.isFinite(v)
                    );

                    if (entries.length === 0) {
                        if (!cancelled) {
                            setByShipment((prev) => ({
                                ...prev,
                                [row.shipmentId]: { status: 'ok', total: 0 }
                            }));
                        }
                        return;
                    }

                    const date = row.shipmentDate ? new Date(row.shipmentDate) : new Date();
                    let total = 0;
                    const missing: string[] = [];

                    for (const [cur, amount] of entries) {
                        const from = cur.toUpperCase();
                        if (from === base) {
                            total += amount;
                            continue;
                        }
                        const mult = await resolveFxMultiplier(from, base, date, cache);
                        if (mult == null) {
                            missing.push(from);
                            continue;
                        }
                        total += amount * mult;
                    }

                    if (cancelled) return;
                    setByShipment((prev) => ({
                        ...prev,
                        [row.shipmentId]:
                            missing.length === 0
                                ? { status: 'ok', total: Math.round(total * 100) / 100 }
                                : missing.length === entries.length
                                ? { status: 'error' }
                                : {
                                      status: 'partial',
                                      total: Math.round(total * 100) / 100,
                                      missingCurrencies: missing
                                  }
                    }));
                })
            );
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [rows, baseCurrency]);

    return byShipment;
}
